/**
 * Shared admin fetch helper — extracted so every admin page doesn't paste the
 * same 14-line apiFetch boilerplate. Drop-in replacement for the inline helpers
 * in AdminSubscriptionAnalytics.tsx / AdminModelIntelligence.tsx / LLMHealthMonitor.tsx.
 *
 * Usage:
 *   import { adminFetch } from '@/utils/adminApi';
 *   const data = await adminFetch<Overview>('/api/admin/analytics/overview?range=30d');
 *
 * Or with React Query:
 *   useQuery({
 *     queryKey: ['analytics', 'overview', range],
 *     queryFn: () => adminFetch<Overview>(`/api/admin/analytics/overview?range=${range}`),
 *   });
 */
import { storage } from '../services/storage';
import { API_BASE_URL } from './apiConfig';

export class AdminApiError extends Error {
  status: number;
  body: string;
  constructor(status: number, body: string, message: string) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * The one place an admin request is authenticated: the admin session token (no request
 * at all without one), the absolute API origin, and a non-2xx answer thrown as an
 * AdminApiError carrying the server's body. Returns the raw Response for callers that
 * need more than JSON (a file download); `adminFetch` is the JSON form.
 */
export async function adminRequest(path: string, init?: RequestInit): Promise<Response> {
  const token = await storage.getItem('admin_token');
  if (!token) {
    throw new AdminApiError(401, '', 'Admin token not found. Please log in again.');
  }

  // A file upload (FormData) sets its own multipart Content-Type with its boundary: forcing JSON
  // on it would leave the server unable to read the file (Codes PR-8: the Summer50 CSV import).
  const isForm = typeof FormData !== 'undefined' && init?.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(isForm ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.text();
    throw new AdminApiError(res.status, body, `API ${res.status}: ${body || res.statusText}`);
  }
  return res;
}

export async function adminFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await adminRequest(path, init);
  return (await res.json()) as T;
}

/**
 * POST/PATCH helper with JSON body serialization.
 */
export async function adminMutate<T>(
  path: string,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  body?: unknown,
): Promise<T> {
  return adminFetch<T>(path, {
    method,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}
